import React, { useEffect, useRef, useState } from 'react';
import {
  AutoComplete,
  type AutoCompleteCompleteEvent,
  type AutoCompleteProps,
} from 'primereact/autocomplete';
import { ReactComponent as ClearIcon } from '../../../assets/shared/ic-shared-clear.svg';
import { ReactComponent as DownArrowIcon } from '../../../assets/shared/ic-shared-down-arrow.svg';
import { Spinner, SpinnerColor } from '../Spinner';
import { ItemTemplate } from './ItemTemplate';
import { LiliError } from '../LiliError';
import { useAsyncAutoCompleteLogic } from './useAsyncAutoCompleteLogic';

export interface Suggestion {
  value: string;
  title: string;
  data?: unknown;
}

export interface LiliAsyncAutoCompleteProps extends Omit<
  AutoCompleteProps,
  'onChange' | 'onFocus' | 'onBlur' | 'onSelect'
> {
  placeholder: string;
  label?: string;
  footerTemplate?: React.ReactNode;
  suggestions: Suggestion[];
  selectedSuggestion?: Suggestion | null;
  debounceTime?: number;
  isLoading: boolean;
  onSearch: (searchQuery: string) => void;
  onSelect?: (suggestion: Suggestion) => void;
  onLoadMore?: (searchQuery: string) => void;
  onClear?: () => void;
  autoFocus?: boolean;
  isPublic?: boolean;
  listMaxHeight?: string;
  inputTestId?: string;
  onInputChange?: (value: string) => void;
  onBlur?: () => void;
  getSelectDisplayTitle?: (suggestion: Suggestion) => string;
  displayValue?: string;
  errorMessage?: string;
  errorTestId?: string;
  loadingTemplate?: React.ReactNode;
  emptyTemplate?: React.ReactNode;
}

export const LiliAsyncAutoComplete = ({
  placeholder,
  label,
  footerTemplate,
  selectedSuggestion,
  debounceTime = 300,
  suggestions,
  isLoading,
  onSelect,
  onLoadMore,
  onSearch,
  onClear,
  autoFocus = false,
  isPublic,
  listMaxHeight = '400px',
  inputTestId,
  onInputChange,
  onBlur: onExternalBlur,
  getSelectDisplayTitle,
  displayValue,
  errorMessage,
  errorTestId,
  loadingTemplate,
  emptyTemplate,
  disabled,
  ...otherProps
}: LiliAsyncAutoCompleteProps) => {
  const [panelMaxWidth, setPanelMaxWidth] = useState<number>(400);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const editedAfterSelect = useRef(false);
  const elPanel = useRef<any>(null);
  const observerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { firstItemRef, lastItemRef, observerRef, searchValue, setSearchValue, inputRef } =
    useAsyncAutoCompleteLogic(selectedSuggestion, displayValue);

  const handleLoadMore = () => {
    observerRef.current?.disconnect();
    onLoadMore?.(searchValue);
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        handleLoadMore();
      }
    });
  };

  const search = (event: AutoCompleteCompleteEvent) => {
    observerRef.current?.disconnect();
    const searchQuery = event.query;
    setSearchValue(searchQuery);
    onSearch(searchQuery);
    if (firstItemRef.current) firstItemRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelect = (suggestion: Suggestion) => {
    editedAfterSelect.current = false;
    onSelect?.(suggestion);
    setTimeout(() => {
      elPanel.current?.hide();
    }, 250);
  };

  const isSuggestionSelected = (item: Suggestion) => {
    if (!selectedSuggestion || item.value !== selectedSuggestion.value) return false;
    if (selectedSuggestion.data !== undefined || item.data !== undefined) {
      return item.data === selectedSuggestion.data;
    }
    return item.title === selectedSuggestion.title;
  };

  const itemTemplate = (item: Suggestion) => {
    return (
      <ItemTemplate
        item={item}
        isFirstItem={item.value === suggestions?.[0]?.value}
        firstItemRef={firstItemRef}
        lastItemRef={lastItemRef}
        isLastItem={item.value === suggestions?.[suggestions.length - 1]?.value}
        isSelected={isSuggestionSelected(item)}
        onSelect={handleSelect}
        setSearchValue={(value) => setSearchValue(value)}
        getSelectDisplayTitle={getSelectDisplayTitle}
      />
    );
  };

  const onBlur = () => {
    if (selectedSuggestion && !editedAfterSelect.current) {
      setSearchValue(selectedSuggestion.title);
    }
    onExternalBlur?.();
  };

  const handleClear = () => {
    editedAfterSelect.current = false;
    setSearchValue('');
    onClear?.();
    if (panelTimeoutRef.current) clearTimeout(panelTimeoutRef.current);
    panelTimeoutRef.current = setTimeout(() => {
      elPanel.current?.hide();
    }, 250);
  };

  const isInputFocused = () => inputRef.current === document.activeElement;

  const handleFocus = () => {
    if (panelTimeoutRef.current) clearTimeout(panelTimeoutRef.current);
    panelTimeoutRef.current = setTimeout(() => {
      if (suggestions.length > 0) {
        elPanel.current?.show();
      }
    }, 250);
  };

  useEffect(() => {
    if (inputRef?.current) {
      setPanelMaxWidth(inputRef?.current?.clientWidth || 400);
    }
  }, [inputRef]);

  const handleShow = () => {
    setIsPanelOpen(true);
    setupObserver();
  };

  const handleHide = () => {
    setIsPanelOpen(false);
  };

  const setupObserver = () => {
    if (observerTimeoutRef.current) clearTimeout(observerTimeoutRef.current);
    observerTimeoutRef.current = setTimeout(() => {
      if (typeof document === 'undefined') return;
      const listElement = document.querySelector('[data-pc-section="list"]');
      const options = {
        root: listElement,
        rootMargin: '0px',
        threshold: 0.5,
      };
      observerRef.current?.disconnect();
      observerRef.current = new IntersectionObserver(handleIntersection, options);
      if (lastItemRef.current) observerRef.current.observe(lastItemRef.current);
    }, 10);
  };

  useEffect(
    () => () => {
      if (observerTimeoutRef.current) clearTimeout(observerTimeoutRef.current);
      if (panelTimeoutRef.current) clearTimeout(panelTimeoutRef.current);
    },
    []
  );

  useEffect(() => {
    if (isInputFocused() && suggestions.length > 0) {
      elPanel.current?.show();
    }
    setupObserver();
  }, [suggestions]);

  const showLoadingPanel = isLoading && searchValue.trim().length > 0 && !!loadingTemplate;
  const showEmptyPanel =
    !isLoading && suggestions.length === 0 && searchValue.trim().length > 0 && !!emptyTemplate;

  return (
    <div className='w-full min-h-[80px]'>
      <div className='relative' id={label ? 'LiliFloatingWrapper' : undefined}>
        <div id={label ? 'LiliFloatinginputWrapper' : undefined}>
          <AutoComplete
            pt={{
              panel: {
                style: {
                  maxWidth: `${panelMaxWidth}px`,
                  overflow: 'auto',
                  maxHeight: listMaxHeight,
                },
              },
              input: {
                root: {
                  ...(!isPublic && { 'data-private': 'lipsum' }),
                  ...(inputTestId && { 'data-testid': inputTestId }),
                },
              },
              loadingIcon: {
                className: 'hidden',
                style: { display: 'none !important' },
              },
              list: {
                style: {
                  overflow: 'scroll',
                  maxHeight: listMaxHeight,
                  ...(footerTemplate ? { paddingBottom: '50px' } : {}),
                },
              },
            }}
            autoFocus={autoFocus}
            ref={elPanel}
            inputClassName={`pl-[16px] w-full pr-[72px] rounded-[8px] outline-none text-[16px] shadow-none ${
              label ? 'pt-[24px] pb-[8px]' : 'py-[14px]'
            } ${
              disabled
                ? 'bg-surface-primary-bw-02 cursor-default border-none text-text-main-tertiary'
                : `hover:bg-surface-primary-bw-01 border focus:border-outline-brand-primary placeholder:text-text-main-tertiary text-text-main-primary ${
                    errorMessage
                      ? '!border-outline-negative-primary'
                      : 'border-outline-primary-bw-04'
                  }`
            }`}
            className='w-full'
            panelClassName='mt-[5px] bg-surface-primary-bw-00 md:rounded-[8px] border border-outline-primary-bw-03 shadow-lg z-[9999]'
            unstyled
            delay={debounceTime}
            placeholder={label ? ' ' : placeholder}
            value={searchValue}
            suggestions={suggestions}
            completeMethod={search}
            itemTemplate={itemTemplate}
            panelFooterTemplate={footerTemplate || null}
            onChange={(e) => {
              const value = e.value;
              if (typeof value === 'string') {
                if (selectedSuggestion && value !== selectedSuggestion.title) {
                  editedAfterSelect.current = true;
                }
                setSearchValue(value);
                onInputChange?.(value);
              }
              if (!value) {
                setSearchValue('');
                onSearch('');
                onInputChange?.('');
              }
            }}
            scrollHeight={listMaxHeight}
            onFocus={handleFocus}
            onShow={handleShow}
            onHide={handleHide}
            onBlur={onBlur}
            inputRef={inputRef}
            disabled={disabled}
            {...otherProps}
          />
        </div>
        {label && (
          <p
            id='floatingLabel'
            className='absolute left-[16px] top-1/2 -translate-y-1/2 text-[16px] text-text-main-tertiary pointer-events-none transition-all ease-out duration-200'>
            {label}
          </p>
        )}
        {!disabled && (
          <div className='absolute right-[16px] top-[50%] transform -translate-y-1/2 flex items-center gap-[6px] z-10'>
            {searchValue && !isLoading && (
              <button onClick={handleClear} type='button'>
                <ClearIcon className='text-text-main-tertiary w-[24px]' />
              </button>
            )}
            {isLoading ? (
              <Spinner
                height='20px'
                width='20px'
                spinnerColor={SpinnerColor.Blue}
                containerClassName='!p-0'
              />
            ) : suggestions.length > 0 ? (
              <button
                type='button'
                onClick={() => {
                  if (isPanelOpen) {
                    elPanel.current?.hide();
                  } else {
                    inputRef.current?.focus();
                    onSearch(searchValue);
                  }
                }}>
                <DownArrowIcon
                  className={`text-text-main-tertiary w-[24px] transition-transform duration-200 ${isPanelOpen ? 'rotate-180' : ''}`}
                />
              </button>
            ) : null}
          </div>
        )}
      </div>
      {(showLoadingPanel || showEmptyPanel) && (
        <div className='mt-[5px] bg-surface-primary-bw-00 rounded-[8px] border border-outline-primary-bw-03 shadow-lg z-[9999] px-[16px] py-[12px]'>
          {showLoadingPanel ? loadingTemplate : emptyTemplate}
        </div>
      )}
      <LiliError errorMessage={errorMessage} data-testid={errorTestId} />
    </div>
  );
};
